import React, { useState, useEffect } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import {
  Grid,
  Button,
  Paper,
  Typography,
  useTheme,
  Box,
  Stepper,
  Step,
  StepLabel,
  MobileStepper,
  useMediaQuery,
  CardMedia,
  Card,
} from "@mui/material";
import RecipeDetailsForm from "../Components/Forms/RecipeDetailsForm";
import NutritionForm from "../Components/Forms/NutritionForm";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useRecipeValidation } from "../Context/RecipeValidationContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { useNotification } from "../Context/notificationContext";
import BreadCrumb from "../Components/BreadCrumb";
import { getRecipeImage, IMAGES_UPLOAD_API_URL } from "../constants";

const ADD_RECIPE = gql`
  mutation AddRecipe(
    $recipeInput: AddRecipeInput!
    $nutrition: NutritionInput!
  ) {
    addRecipe(recipeInput: $recipeInput, nutrition: $nutrition) {
      id
      message
    }
  }
`;

const UPDATE_RECIPE = gql`
  mutation UpdateRecipe(
    $id: ID!
    $recipeInput: UpdateRecipeInput!
    $nutrition: NutritionInput!
  ) {
    updateRecipe(id: $id, recipeInput: $recipeInput, nutrition: $nutrition) {
      id
      message
    }
  }
`;

// Yup validation schema
export const recipeSchema = yup.object().shape({
  recipe_name: yup.string().required("Recipe name is required"),
  recipe_category: yup
    .array()
    .min(1, "At least one recipe category is required"),
  recipe_description: yup
    .string()
    .required("Recipe description is required")
    .nullable(),
  nutrition: yup.object().shape({
    calories: yup
      .number()
      .min(0, "Calories must be zero or positive")
      .typeError("Calories must be a number"),

    carbohydrate: yup
      .number()
      .min(0, "Carbohydrates must be zero or positive")
      .typeError("Carbohydrates must be a number"),

    protein: yup
      .number()
      .min(0, "Protein must be zero or positive")
      .typeError("Protein must be a number"),

    fats: yup
      .number()
      .min(0, "Fats must be zero or positive")
      .typeError("Fats must be a number"),
  }),
});

const AddNewRecipe = ({ defaultValues, recipeId, isDownloaded }) => {
  const { showNotification } = useNotification();
  const theme = useTheme(); // Get the theme from Material-UI
  // Convert comma-separated strings to arrays for form default values
  const formattedDefaultValues = {
    ...defaultValues,
    recipe_type: defaultValues?.recipe_type ? defaultValues.recipe_type : "",
    recipe_category: defaultValues?.recipe_category
      ? defaultValues.recipe_category.split(", ")
      : [],
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Recipe Details", "Nutrition Information", "Review & Submit"];
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isValid = useRecipeValidation(parseInt(recipeId));
  const isAuthor = isValid?.isAuthor || false;

  const methods = useForm({
    resolver: yupResolver(recipeSchema),
    defaultValues: formattedDefaultValues, // Use the formatted default values
  });

  const [addRecipe, { loading: adding }] = useMutation(ADD_RECIPE);
  const [updateRecipe, { loading: updating }] = useMutation(UPDATE_RECIPE);
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    if (defaultValues && !isAuthor) {
      toast.error("You are not allowed to make any changes to this recipe.");
      return;
    }

    try {
      const recipeInput = {
        recipe_name: data.recipe_name,
        recipe_type: data.recipe_type,
        recipe_category: data.recipe_category.join(", "),
        recipe_description: data.recipe_description,
      };

      const nutrition = {
        calories: parseFloat(data.nutrition.calories || 0),
        carbohydrate: parseFloat(data.nutrition.carbohydrate || 0),
        protein: parseFloat(data.nutrition.protein || 0),
        fats: parseFloat(data.nutrition.fats || 0),
      };

      let response;
      if (defaultValues) {
        // Update existing recipe
        response = await updateRecipe({
          variables: { id: defaultValues.id, recipeInput, nutrition },
        });
        toast.success("Recipe updated successfully!");
      } else {
        // Add new recipe
        response = await addRecipe({
          variables: { recipeInput, nutrition },
        });
        toast.success("Recipe added successfully!");
      }

      if (response?.data?.addRecipe?.id || response?.data?.updateRecipe?.id) {
        handleImageUpload(
          response?.data?.addRecipe?.id || response?.data?.updateRecipe?.id
        );
        setTimeout(() => {
          navigate(
            `/action-manager/${
              response.data.addRecipe?.id || response.data.updateRecipe?.id
            }`
          );
        }, 2000);
      }
    } catch (err) {
      console.error("Error processing recipe:", err);
      toast.error("Failed to process recipe. Please try again.");
    }
  };

  const [coverFile, setCoverFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(
    defaultValues?.recipe_image
      ? getRecipeImage(defaultValues.recipe_image)
      : null
  );

  const handleCoverFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCoverFile(file); // Store the file object for upload

      // Create preview URL only if file exists
      if (file instanceof Blob) {
        setCoverImagePreview(URL.createObjectURL(file));
      }
    }
  };

  const handleImageUpload = async (recipe_id) => {
    if (!coverFile && !defaultValues?.recipe_image) {
      showNotification("error", "Please upload a cover image for your recipe");
      return;
    }

    // If no new file is selected when updating, keep existing image
    if (!coverFile && defaultValues?.recipe_image) {
      return; // No need to upload again
    }
    if (!coverFile) return;

    if (!["image/png", "image/jpeg", "image/jpg"].includes(coverFile.type)) {
      showNotification("error", "Only PNG, JPEG, JPG files are allowed");
      return;
    }

    if (coverFile.size > 10 * 1024 * 1024) {
      showNotification("error", "File size must be less than 10MB");
      return;
    }

    const formData = new FormData();
    formData.append("image", coverFile);

    try {
      const response = await fetch(`${IMAGES_UPLOAD_API_URL}${recipe_id}`, {
        method: "POST",
        body: formData,
        baseUrl: "user",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
    } catch (error) {
      console.log("error", error);
      showNotification("error", error.message);
    }
  };

  return (
    <>
      <BreadCrumb
        links={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "My Recipes", href: "/my-recipes" },
        ]}
        currentPage={defaultValues ? "Modify Recipe" : "Create Recipe"}
      />
      <ToastContainer />

      <Box sx={{ width: "100%", mt: 4 }}>
        {!isMobile ? (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      typography: "body1",
                      fontWeight: 600,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          <MobileStepper
            variant="dots"
            steps={3}
            position="static"
            activeStep={activeStep}
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
            nextButton={null}
            backButton={null}
          />
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormProvider {...methods}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: "background.paper",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.05)",
              }}
            >
              {activeStep === 0 && (
                <RecipeDetailsForm
                  handleCoverFileChange={handleCoverFileChange}
                  coverFile={coverImagePreview}
                  defaultValues={defaultValues}
                />
              )}

              {activeStep === 1 && (
                <Box className="nutrition-form-step">
                  <NutritionForm />

                  {/* Simple spacing at the bottom to ensure buttons are visible */}
                  <Box sx={{ height: { xs: "100px", sm: "60px" } }} />
                </Box>
              )}

              {activeStep === 2 && (
                <ReviewStep
                  coverFile={coverImagePreview}
                  isDownloaded={isDownloaded}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 4,
                  position:
                    activeStep === 1 && isMobile ? "sticky" : "relative",
                  bottom: activeStep === 1 && isMobile ? 0 : "auto",
                  zIndex: activeStep === 1 && isMobile ? 10 : 1,
                  bgcolor: "background.paper",
                  py: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep((s) => s - 1)}
                  disabled={activeStep === 0}
                  startIcon={<KeyboardArrowLeft />}
                >
                  Back
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={methods.handleSubmit(onSubmit)}
                    disabled={adding || updating}
                    sx={{ px: 4 }}
                  >
                    {defaultValues ? "Update Recipe" : "Submit Recipe"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => {
                      // For all steps, simply move to the next step
                      setActiveStep((s) => s + 1);

                      // For better mobile experience, scroll to top
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    endIcon={<KeyboardArrowRight />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Paper>
          </FormProvider>
        </LocalizationProvider>
      </Box>
    </>
  );
};

export default AddNewRecipe;

const ReviewStep = ({ coverFile, isDownloaded }) => {
  const { getValues } = useFormContext();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {isDownloaded ? "Review Your Downloaded Recipe" : "Review Your Recipe"}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardMedia
              component="img"
              image={coverFile || "/placeholder-16x9.png"}
              alt="Recipe Preview"
              sx={{ height: 200, objectFit: "cover" }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            <DetailItem label="Recipe Name" value={getValues("recipe_name")} />
            <DetailItem label="Type" value={getValues("recipe_type")} />
            <DetailItem
              label="Categories"
              value={getValues("recipe_category").join(", ")}
            />
            <DetailItem
              label="Serving Size"
              value={`${getValues("serving")} People`}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Description
          </Typography>
          <Box
            dangerouslySetInnerHTML={{
              __html: getValues("recipe_description"),
            }}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              minHeight: 100,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const DetailItem = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5 }}>
      {value || "-"}
    </Typography>
  </Box>
);
